/**
 * resolveNextAction — cerveau du cockpit de vente.
 * ────────────────────────────────────────────────
 * Prend l'état complet d'un dossier + le résumé paiement et retourne
 * la SEULE prochaine action à afficher au commercial.
 *
 * Contrat de retour (props NextActionCard) :
 *   {
 *     tone       : "accent" | "danger" | "success" | "neutral",
 *     eyebrow    : string,
 *     title      : string,
 *     description: string | null,
 *     icon       : LucideIcon | null,
 *     primaryCta : { label, onClick, icon? } | null,
 *     secondaryCta: { label, onClick, icon? } | null,
 *   }
 *
 * Aucune dépendance UI ici — juste de la logique produit pure.
 * L'objectif : que le vendeur comprenne en < 3 secondes quoi faire.
 */
import {
  AlertTriangle, CheckCircle2, Wallet, Bell, ArrowRight,
  Truck, PartyPopper, PlayCircle, ClipboardCheck, Clock,
} from "lucide-react";
import { STEP_STATUS } from "@/data/mockData";

const fmtMoney = (v, currency = "EUR") => {
  if (v == null || v === "") return "";
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency || "EUR",
      maximumFractionDigits: 0,
    }).format(v);
  } catch {
    return `${v} ${currency}`;
  }
};

export function resolveNextAction({
  vehicle,
  payment,
  delivery,
  actions = {},
}) {
  const {
    openStep, openPaymentConfirm, openPaymentRemind,
    openEdit, openMessagesTab, openDeliverySlots,
  } = actions;
  const steps = vehicle.steps || [];
  const summary = vehicle.workflowSummary || {};
  const gate = summary.delivery_gate || {};
  const pay = payment?.summary || {};
  const sched = delivery || vehicle.deliveryScheduling || null;

  // ── 0. Dossier finalisé → célébration ────────────────────────────────
  if (vehicle.isFinished) {
    return {
      tone: "success",
      eyebrow: "VENTE TERMINÉE",
      title: "Bravo — le dossier est clôturé",
      description: "Toutes les étapes sont validées et le véhicule est livré.",
      icon: PartyPopper,
      primaryCta: null,
      secondaryCta: null,
    };
  }

  // ── 1. Paiement déclaré par le client → à confirmer (priorité forte) ─
  if (pay.status === "declared") {
    const amount = pay.declared_amount ?? pay.amount_due;
    return {
      tone: "accent",
      eyebrow: "À CONFIRMER",
      title: `Confirmer ${amount ? fmtMoney(amount, pay.currency) : "le paiement"}`,
      description:
        "Le client déclare avoir payé. Vérifiez la réception puis validez pour débloquer la suite.",
      icon: Wallet,
      primaryCta: openPaymentConfirm
        ? { label: "Valider le paiement", onClick: openPaymentConfirm, icon: CheckCircle2 }
        : null,
      secondaryCta: openPaymentRemind
        ? { label: "Contacter le client", onClick: openPaymentRemind, icon: Bell }
        : null,
    };
  }

  // ── 2. Paiement en attente et bloque la livraison ────────────────────
  if (pay.status === "pending" && pay.delivery_blocked) {
    const amount = pay.amount_due;
    return {
      tone: "accent",
      eyebrow: "PAIEMENT ATTENDU",
      title: amount ? `Recevoir ${fmtMoney(amount, pay.currency)}` : "Recevoir le paiement client",
      description:
        "Le règlement est requis pour poursuivre le dossier. Marquez-le comme reçu dès qu'il est encaissé.",
      icon: Wallet,
      primaryCta: openPaymentConfirm
        ? { label: "Paiement reçu", onClick: openPaymentConfirm, icon: CheckCircle2 }
        : null,
      secondaryCta: openPaymentRemind
        ? { label: "Rappeler", onClick: openPaymentRemind, icon: Bell }
        : null,
    };
  }

  // ── 3. Une étape bloque la livraison (hors paiement) ─────────────────
  const blockers = gate.blocked_by || [];
  if (blockers.length > 0) {
    const first = blockers[0];
    const stepObj = steps.find((s) => s.key === first.key);
    return {
      tone: "danger",
      eyebrow: "BLOCAGE À LEVER",
      title: `Débloquer : ${first.label}`,
      description: first.reason
        ? first.reason
        : "Cette étape empêche d'avancer. Traitez-la pour libérer la suite.",
      icon: AlertTriangle,
      primaryCta:
        openStep && stepObj
          ? { label: "Ouvrir l'étape", onClick: () => openStep(stepObj), icon: ArrowRight }
          : null,
      secondaryCta: null,
    };
  }

  // ── 4. Étape en cours → à terminer ───────────────────────────────────
  const active = steps.find((s) => s.status === STEP_STATUS.IN_PROGRESS);

  // ── 4bis. Livraison en cours OU à démarrer → planification créneaux ─
  // Priorité forte : dès que l'étape "livraison" arrive au premier plan,
  // le cockpit doit guider vers la planification des créneaux, PAS ouvrir
  // la fiche générique de l'étape.
  const nextKeyForDelivery = summary.next_action?.key;
  const isDeliveryTurn =
    (active && active.key === "livraison") ||
    nextKeyForDelivery === "livraison" ||
    (!active && steps.find((s) => s.key === "livraison" && s.status === STEP_STATUS.UPCOMING));

  if (isDeliveryTurn) {
    if (!sched || sched.status === "none" || sched.status === "canceled") {
      return {
        tone: "accent",
        eyebrow: "PROCHAINE ACTION",
        title: "Proposer des créneaux",
        description:
          "Le véhicule est prêt. Envoyez au client 2 ou 3 créneaux de livraison — il choisira en un tap.",
        icon: Truck,
        primaryCta: openDeliverySlots
          ? { label: "Proposer des créneaux", onClick: openDeliverySlots, icon: ArrowRight }
          : null,
        secondaryCta: openDeliverySlots
          ? { label: "Fixer directement", onClick: openDeliverySlots }
          : null,
      };
    }
    if (sched.status === "proposed") {
      const count = sched.proposals?.length || 0;
      return {
        tone: "neutral",
        eyebrow: "EN ATTENTE DU CLIENT",
        title: `${count} créneaux envoyés au client`,
        description:
          "Le client va choisir sa date de livraison. Sa décision arrivera automatiquement ici.",
        icon: Clock,
        primaryCta: openDeliverySlots
          ? { label: "Modifier les créneaux", onClick: openDeliverySlots }
          : null,
        secondaryCta: null,
      };
    }
    if (sched.status === "confirmed" && vehicle.deliveryDate) {
      return {
        tone: "success",
        eyebrow: "LIVRAISON PROGRAMMÉE",
        title: `Livraison le ${vehicle.deliveryDate}${vehicle.deliveryTime ? " · " + vehicle.deliveryTime : ""}`,
        description: sched.location
          ? `Rendez-vous ${sched.location}. Préparez la remise du véhicule.`
          : "Préparez la remise du véhicule au client.",
        icon: Truck,
        primaryCta: openMessagesTab
          ? { label: "Informer le client", onClick: openMessagesTab, icon: ArrowRight }
          : null,
        secondaryCta: openDeliverySlots
          ? { label: "Modifier le rendez-vous", onClick: openDeliverySlots }
          : null,
      };
    }
  }

  if (active) {
    return {
      tone: "accent",
      eyebrow: "EN COURS",
      title: active.label || active.title,
      description:
        active.description ||
        "Cette étape est active. Terminez-la pour faire avancer le dossier.",
      icon: ClipboardCheck,
      primaryCta: openStep
        ? { label: "Ouvrir l'étape", onClick: () => openStep(active), icon: ArrowRight }
        : null,
      secondaryCta: null,
    };
  }

  // ── 5. Prochaine étape à démarrer ────────────────────────────────────
  const nextKey = summary.next_action?.key;
  const nextStep =
    (nextKey && steps.find((s) => s.key === nextKey)) ||
    steps.find((s) => s.status === STEP_STATUS.UPCOMING);
  if (nextStep) {
    return {
      tone: "accent",
      eyebrow: "PROCHAINE ÉTAPE",
      title: `Lancer : ${nextStep.label || nextStep.title}`,
      description: "Prête à démarrer dès que vous êtes prêt.",
      icon: PlayCircle,
      primaryCta: openStep
        ? { label: "Démarrer l'étape", onClick: () => openStep(nextStep), icon: ArrowRight }
        : null,
      secondaryCta: null,
    };
  }

  // ── 6. Toutes les étapes ok mais la livraison n'est pas planifiée ────
  if (!vehicle.deliveryDate && (!sched || sched.status === "none" || sched.status === "canceled")) {
    return {
      tone: "accent",
      eyebrow: "PROCHAINE ACTION",
      title: "Proposer des créneaux",
      description:
        "Le véhicule est prêt. Envoyez au client 2 ou 3 créneaux de livraison — il choisira en un tap.",
      icon: Truck,
      primaryCta: openDeliverySlots
        ? { label: "Proposer des créneaux", onClick: openDeliverySlots, icon: ArrowRight }
        : openEdit
        ? { label: "Fixer la livraison", onClick: openEdit, icon: ArrowRight }
        : null,
      secondaryCta: openEdit
        ? { label: "Fixer directement", onClick: openEdit }
        : null,
    };
  }

  // ── 6bis. Créneaux envoyés → en attente du choix client ──────────────
  if (sched?.status === "proposed") {
    const count = sched.proposals?.length || 0;
    return {
      tone: "neutral",
      eyebrow: "EN ATTENTE DU CLIENT",
      title: `${count} créneaux envoyés au client`,
      description:
        "Le client va choisir sa date de livraison. Sa décision arrivera automatiquement ici.",
      icon: Clock,
      primaryCta: openDeliverySlots
        ? { label: "Modifier les créneaux", onClick: openDeliverySlots }
        : null,
      secondaryCta: null,
    };
  }

  // ── 7. Livraison programmée → informer le client ─────────────────────
  return {
    tone: "success",
    eyebrow: "LIVRAISON PROGRAMMÉE",
    title: `Livraison le ${vehicle.deliveryDate}${vehicle.deliveryTime ? " · " + vehicle.deliveryTime : ""}`,
    description:
      vehicle.deliveryLocation
        ? `Rendez-vous ${vehicle.deliveryLocation}. Préparez la remise du véhicule.`
        : "Préparez la remise du véhicule au client.",
    icon: Truck,
    primaryCta: openMessagesTab
      ? { label: "Informer le client", onClick: openMessagesTab, icon: ArrowRight }
      : null,
    secondaryCta: openDeliverySlots
      ? { label: "Modifier le rendez-vous", onClick: openDeliverySlots }
      : openEdit
      ? { label: "Modifier la date", onClick: openEdit }
      : null,
  };
}
